import type { ApiResponse } from '@/libs/interfaces'
import type {
  CategoryListParams,
  CategoryListResponse,
  ProductCategory,
  ServiceCategory,
} from '@/services/api/categories'

import { mockServiceCategories as seedServiceCategories } from '../fixtures/categories'
import { mockProducts } from '../fixtures/products'
import { mockServices } from '../fixtures/services'
import { delay, genID, matchesSearch } from '../helpers'
import { createEntityStore } from '../store'

const toServiceCategory = (
  categoryID: string,
  categoryName: string,
  index: number,
): ServiceCategory => ({
  id: categoryID,
  name: categoryName,
  description: seedServiceCategories[index]?.serviceCategoryDescription,
  serviceCount: mockServices.filter((service) => service.serviceCategoryID === categoryID).length,
  isActive: true,
  colorForService: mockServices.find((service) => service.serviceCategoryID === categoryID)
    ?.colorForService,
})

const toProductCategory = (categoryID: string, categoryName: string): ProductCategory => ({
  id: categoryID,
  name: categoryName,
  productCount: mockProducts.filter((product) => product.productCategoryID === categoryID).length,
  isActive: true,
})

const serviceCategoryStore = createEntityStore<ServiceCategory, 'id'>(
  seedServiceCategories.map((category, index) =>
    toServiceCategory(category.serviceCategoryID, category.serviceCategoryName, index),
  ),
  'id',
)

const productCategoryStore = createEntityStore<ProductCategory, 'id'>(
  seedServiceCategories.map((category) =>
    toProductCategory(category.serviceCategoryID, category.serviceCategoryName),
  ),
  'id',
)

const toApiResponse = <T>(data: T): ApiResponse<T> => ({ data, status: 200 })

const filterCategories = <T extends { name: string }>(
  items: T[],
  params?: CategoryListParams,
): T[] => items.filter((item) => matchesSearch(item, params?.search, ['name']))

export const categoriesApiMock = {
  listServiceCategories: async (
    params?: CategoryListParams,
  ): Promise<ApiResponse<CategoryListResponse<ServiceCategory>>> => {
    const items = filterCategories(serviceCategoryStore.getAll(), params)
    return delay(
      toApiResponse({
        data: items,
        total: items.length,
        page: params?.page ?? 1,
        limit: params?.limit ?? items.length,
      }),
    )
  },
  getServiceCategory: async (id: string): Promise<ApiResponse<ServiceCategory>> =>
    delay(toApiResponse(serviceCategoryStore.getById(id) ?? ({} as ServiceCategory))),
  createServiceCategory: async (
    data: Omit<ServiceCategory, 'id'>,
  ): Promise<ApiResponse<ServiceCategory>> => {
    const category: ServiceCategory = {
      id: genID('service-category'),
      ...data,
    }
    serviceCategoryStore.insert(category)
    return delay(toApiResponse(category))
  },
  updateServiceCategory: async (
    id: string,
    data: Partial<ServiceCategory>,
  ): Promise<ApiResponse<ServiceCategory>> => {
    const updated = serviceCategoryStore.update(id, data)
    return delay(toApiResponse(updated ?? ({} as ServiceCategory)))
  },
  deleteServiceCategory: async (id: string): Promise<ApiResponse<void>> => {
    serviceCategoryStore.remove(id)
    return delay(toApiResponse(undefined))
  },
  listProductCategories: async (
    params?: CategoryListParams,
  ): Promise<ApiResponse<CategoryListResponse<ProductCategory>>> => {
    const items = filterCategories(productCategoryStore.getAll(), params)
    return delay(
      toApiResponse({
        data: items,
        total: items.length,
        page: params?.page ?? 1,
        limit: params?.limit ?? items.length,
      }),
    )
  },
  getProductCategory: async (id: string): Promise<ApiResponse<ProductCategory>> =>
    delay(toApiResponse(productCategoryStore.getById(id) ?? ({} as ProductCategory))),
  createProductCategory: async (
    data: Omit<ProductCategory, 'id'>,
  ): Promise<ApiResponse<ProductCategory>> => {
    const category: ProductCategory = {
      id: genID('product-category'),
      ...data,
    }
    productCategoryStore.insert(category)
    return delay(toApiResponse(category))
  },
  updateProductCategory: async (
    id: string,
    data: Partial<ProductCategory>,
  ): Promise<ApiResponse<ProductCategory>> => {
    const updated = productCategoryStore.update(id, data)
    return delay(toApiResponse(updated ?? ({} as ProductCategory)))
  },
  deleteProductCategory: async (id: string): Promise<ApiResponse<void>> => {
    productCategoryStore.remove(id)
    return delay(toApiResponse(undefined))
  },
}
